
import { useState } from 'react';
import Card from '../../../components/base/Card';

export default function AboutSection() {
  const [selectedCompetency, setSelectedCompetency] = useState<number | null>(null);

  const competencies = [
    { 
      name: 'Organic Social Media Strategy', 
      icon: 'ri-hashtag',
      description: 'Organic social is about more than posting trendy content. That might be one of the most fun parts of the job, but a holistic organic social strategy needs so much more.\n\nThe best strategies establish KPIs that both ladder up to larger brand goals and measure unique value from each social media platform. They meet followers on their terms, understanding how they use social media what they want from brand interactions. They employ Social Media Optimization to maximize search results, and they constantly measure and optimize, allowing data to drive strategy forward.'
    },
    { 
      name: 'Paid Media Planning & Strategy', 
      icon: 'ri-advertisement-line',
      description: 'Campaign optimizations, bidding strategies, A/B testing, custom and lookalike audiences, retargeting tactics... Managing a robust paid social strategy is not simply a \'set it and forget it\' proposition.\n\nWith many levers to pull and dynamic variables to consider, it requires both a well-planned approach and nimble execution that\'s reactive to performance trends.'
    },
    { 
      name: 'Analytics, Insights, Optimization', 
      icon: 'ri-bar-chart-line',
      description: 'Numbers never lie, or at least that\'s what we\'re told. But it\'s the context around the data that really tells the story. I call this \'so what?\' reporting, or reporting that goes beyond a data-dump and explains how we got here and where we\'re headed next as a result. Brands should be able to examine a report, dashboard or quarterly business review and know not just whether performance was good or bad, but why.'
    },
    { 
      name: 'Online Brand Management', 
      icon: 'ri-award-line',
      description: 'Social media has become one of the first places consumers go to research a brand or product, and a brand\'s presence beyond its content can go a long way toward earning trust and loyalty.\n\nThe brands that do it best foster relationships with their followers. They converse with their followers rather than talking at them. They participate in conversations with fans and other brands, even if it\'s not their own post. They celebrate 5-star reviews and remedy 1-star reviews. They citations are up-to-date, branding is consistent and brand values are apparent.\n\nWhen it\'s done right, it can be the fastest way to earn new business among curious consumers.'
    },
    { 
      name: 'Copywriting & SEO', 
      icon: 'ri-edit-line',
      description: 'Language will always be a fundamental piece of marketing. A brand\'s voice and tone set the precedent for how a brand can be perceived, and word choice can sway consumer decisions in the blink of an eye.\n\nHowever, in this digital age of marketing, language carries tactical value as well. For many years, SEO has been vital to a brand\'s ability to be discovered by search engine users, and in recent years Social Media Optimization has adopted these same principles for in-platform searches.\n\nMastery of these best practices not only strengthens communication with consumers, but also opens pathways to forge new relationships with prospects.'
    },
    { 
      name: 'Graphic & Video Editing', 
      icon: 'ri-image-edit-line',
      description: 'Social media moves fast, and it is important for social media marketers to be armed with content development skills to stay nimble as trends arise or opportunities reveal themselves.\n\nThis includes a fluency in the Adobe Creative Suite (particularly Photoshop, Illustrator and Premier) as well as quick-edit services like Canva. The best strategists are also prepared with accurate subtitles for video content and alt tags for enhanced accessibility.'
    },
    { 
      name: 'Influencer Campaign Management', 
      icon: 'ri-user-star-line',
      description: 'The right approach to influencer marketing is not simply asking \'who within our budget has the most followers?\' Audience size matters, of course. But so do audience interests and activity, and so does the style and quality of a given influencer\'s presence on social media.\n\nFor instance, if you\'re marketing to college students, partnering with influential students and organizations on campus can gain just as much traction as a single post from a larger celebrity.\n\nInfluencer marketing is not a popularity contest; it\'s a form of modern word-of-mouth marketing. Consumers have always been more influenced by other individuals than by brand marketing, so the best influencer campaigns are the ones that leverage the most meaningful voices to a specific audience, not simply who has the most followers.'
    }
  ];

  const openModal = (index: number) => {
    if (competencies[index].description) {
      setSelectedCompetency(index);
    }
  };

  const closeModal = () => {
    setSelectedCompetency(null);
  };

  return (
    <section 
      id="about" 
      className="py-20 bg-cover bg-center bg-fixed relative"
      style={{
        backgroundImage: `linear-gradient(rgba(255, 255, 255, 0.95), rgba(255, 255, 255, 0.95)), url('https://public.readdy.ai/ai/img_res/4ca7926fa84be252a12fc29f03a0dd65.jpg')`
      }}
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-16">
          <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
            About Me
          </h2>
          <div className="w-20 h-1 bg-blue-600 mx-auto mb-6"></div>
          <p className="text-lg text-gray-600 max-w-2xl mx-auto italic">
            "The more I practice, the luckier I get." - Gary Player
          </p>
        </div>

        <div className="grid lg:grid-cols-2 gap-12 items-start">
          <div>
            <img 
              src="https://static.readdy.ai/image/7e30e953bcb98024829bc0b49eccc224/5a9022afe5f99423a798954e7cc9c537.jfif"
              alt="Professional headshot"
              className="rounded-2xl shadow-2xl w-full object-cover object-top h-full"
            />
          </div>
          
          <div>
            <Card className="h-full bg-white/90 backdrop-blur-sm">
              <div className="flex items-center mb-6">
                <div className="w-12 h-12 flex items-center justify-center bg-blue-100 rounded-full mr-4">
                  <i className="ri-user-star-line text-2xl text-blue-600"></i>
                </div>
                <h3 className="text-2xl font-bold text-gray-900">Marketing & Communications Strategist</h3>
              </div>
              
              <div className="text-gray-600 mb-8 leading-relaxed space-y-4">
                <p>
                  In ten years as a marketing strategist at full-service agencies, I've been privileged to wear many different hats.
                </p>
                <p>
                  I have experience in paid-, owned- and earned-media strategy spanning most of the social media universe, including Facebook, Insta, TikTok, X, LinkedIn, Pinterest, Snapchat and YouTube.
                </p>
                <p>
                  Whether it's working independently as a self-starter or contributing to a collaborative project, I strive to lead by example in any setting.
                </p>
              </div>
              
              <div>
                <h4 className="text-lg font-semibold text-gray-900 mb-6">Core Competencies</h4>
                <div className="grid grid-cols-1 gap-3">
                  {competencies.map((competency, index) => (
                    <div 
                      key={index} 
                      onClick={() => openModal(index)}
                      className={`flex items-center p-3 bg-gradient-to-r from-blue-50/80 to-indigo-50/80 rounded-lg border border-blue-100 hover:from-blue-100/80 hover:to-indigo-100/80 transition-all duration-300 backdrop-blur-sm ${competency.description ? 'cursor-pointer hover:shadow-md' : ''}`}
                    >
                      <div className="w-10 h-10 flex items-center justify-center bg-blue-600 text-white rounded-lg mr-4">
                        <i className={`${competency.icon} text-lg`}></i>
                      </div>
                      <span className="text-gray-800 font-medium flex-1">{competency.name}</span>
                      {competency.description && (
                        <div className="w-6 h-6 flex items-center justify-center">
                          <i className="ri-arrow-right-s-line text-blue-600 text-lg"></i>
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            </Card>
          </div>
        </div>
      </div>

      {/* Modal */}
      {selectedCompetency !== null && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl max-w-2xl w-full max-h-[80vh] overflow-y-auto shadow-2xl">
            <div className="p-6 border-b border-gray-200">
              <div className="flex items-center justify-between">
                <div className="flex items-center">
                  <div className="w-12 h-12 flex items-center justify-center bg-blue-600 text-white rounded-lg mr-4">
                    <i className={`${competencies[selectedCompetency].icon} text-xl`}></i>
                  </div>
                  <h3 className="text-2xl font-bold text-gray-900">
                    {competencies[selectedCompetency].name}
                  </h3>
                </div>
                <button
                  onClick={closeModal}
                  className="w-8 h-8 flex items-center justify-center text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-full transition-colors duration-200"
                >
                  <i className="ri-close-line text-xl"></i>
                </button>
              </div>
            </div>
            <div className="p-6">
              <div className="text-gray-700 leading-relaxed whitespace-pre-line">
                {competencies[selectedCompetency].description}
              </div>
            </div>
          </div>
        </div>
      )}
    </section>
  );
}
